import { getGetClinicQueryKey, getListClinicActivityQueryKey, useUpdateClinic, type ClinicDetail } from '@pa/api/admin'
import { toastApiError } from '@pa/staff'
import { Toggle, useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'

/** S-04 Summary copy for task 24's switch (`pfMessagesEnabled` on #41). */
export const PF_MESSAGES_COPY = {
  title: 'PF internal message notification',
  label: 'PF internal message notification',
  hintOff: 'Off: no Practice Fusion message is sent when a patient books online.',
  hintOn:
    'On: the clinic picks who gets a Practice Fusion message for each online booking, in Settings → PF internal message notification. Needs PF Keeper 0.1.3.',
  doneOn: 'PF internal message notification turned on',
  doneOff: 'PF internal message notification turned off',
}

/**
 * S-04 Summary (task 24, D-81): the per-clinic PF internal message switch, super admin only and off by default.
 * No confirmation either way; saved at once with #41 `{ pfMessagesEnabled }` (audited as `clinic.update`). On a
 * failure the switch stays as it was (it follows the saved clinic) and a toast says why.
 */
export function PfMessagesSwitchCard({ clinic }: { clinic: ClinicDetail }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const update = useUpdateClinic()
  const on = clinic.pfMessagesEnabled

  async function save(next: boolean) {
    try {
      const updated = await update.mutateAsync({ clinicId: clinic.id, data: { pfMessagesEnabled: next } })
      queryClient.setQueryData(getGetClinicQueryKey(clinic.id), updated)
      void queryClient.invalidateQueries({ queryKey: getListClinicActivityQueryKey(clinic.id) })
      toast(next ? PF_MESSAGES_COPY.doneOn : PF_MESSAGES_COPY.doneOff)
    } catch (err) {
      toastApiError(toast, err)
    }
  }

  return (
    <section className="card card-pad form-grid" aria-labelledby="s04-pf-messages">
      <h2 className="form-section-title" id="s04-pf-messages">
        {PF_MESSAGES_COPY.title}
      </h2>
      <div className="row testing-switch">
        <Toggle
          checked={on}
          disabled={update.isPending}
          label={PF_MESSAGES_COPY.label}
          onChange={(next) => {
            void save(next)
          }}
        />
        <span aria-hidden>{PF_MESSAGES_COPY.label}</span>
      </div>
      <p className="hint">{on ? PF_MESSAGES_COPY.hintOn : PF_MESSAGES_COPY.hintOff}</p>
    </section>
  )
}
